import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Container,
  Grid,
  Paper,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Chip
} from '@mui/material';
import {
  Search,
  Clear,
  FilterList,
  Sort
} from '@mui/icons-material';
import {
  setFilters,
  clearFilters,
  selectFilters,
  selectAllTowns,
  selectTownsLoading,
  selectAllMunicipalities,
  selectMunicipalitiesLoading,
  fetchAllMunicipalities
} from '../../store/slices/chitalishtaSlice';

const REGIONS = [
  'Благоевград', 'Бургас', 'Варна', 'Велико Търново', 'Видин',
  'Враца', 'Габрово', 'Добрич', 'Кърджали', 'Кюстендил',
  'Ловеч', 'Монтана', 'Пазарджик', 'Перник', 'Плевен',
  'Пловдив', 'Разград', 'Русе', 'Силистра', 'Сливен',
  'Смолян', 'София', 'София-град', 'Стара Загора', 'Търговище',
  'Хасково', 'Шумен', 'Ямбол'
];

const ChitalishteFilters = ({ sort, handleSortChange, itemsPerPage, handleItemsPerPageChange }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Redux state
  const filters = useSelector(selectFilters);
  const towns = useSelector(selectAllTowns);
  const townsLoading = useSelector(selectTownsLoading);
  const municipalities = useSelector(selectAllMunicipalities);
  const municipalitiesLoading = useSelector(selectMunicipalitiesLoading);

  // Local state за форма
  const [localFilters, setLocalFilters] = useState({
    name: '',
    region: '',
    municipality: '',
    town: ''
  });

  // Зареди общини при монтиране
  useEffect(() => {
    dispatch(fetchAllMunicipalities());
  }, [dispatch]);

  // Синхронизация на локалното състояние с Redux при монтиране
  useEffect(() => {
    if (filters) {
      setLocalFilters({
        name: filters.name || '',
        region: filters.region || '',
        municipality: filters.municipality || '',
        town: filters.town || ''
      });
    }
  }, [filters]);

  const handleLocalFilterChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value || ''
    }));
  };

  const handleApplyFilters = () => {
    // console.log('🔍 Applying filters:', localFilters);
    dispatch(setFilters(localFilters));
  };

  const handleClearFilters = () => {
    // console.log('🧹 Clearing filters');
    setLocalFilters({
      name: '',
      region: '',
      municipality: '',
      town: ''
    });
    dispatch(clearFilters());
  };

  const hasActiveFilters = Object.values(localFilters).some(
    value => value !== null && value !== undefined && value !== ''
  );

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleApplyFilters();
    }
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 3,
      mb: 0
    }}>
      <Container maxWidth="xl">
        <Paper 
          elevation={3}
          sx={{ 
            p: 3,
            borderRadius: 2,
            background: 'white'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            gap: 1
          }}>
            <FilterList sx={{ color: 'primary.main', fontSize: 28 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#2c3e50' }}>
                  Търсене и филтри
                </span>
                {hasActiveFilters && (
                  <Chip 
                    label="Активни" 
                    color="primary" 
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* Търсене по име */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Въведете име на читалище..."
                variant="outlined"
                size="small"
                value={localFilters.name}
                onChange={(e) => handleLocalFilterChange('name', e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>

            {/* Област */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                options={REGIONS}
                value={localFilters.region || null}
                onChange={(event, newValue) => handleLocalFilterChange('region', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Изберете област..."
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            </Grid>

            {/* Община */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                options={municipalities}
                value={localFilters.municipality || null}
                onChange={(event, newValue) => handleLocalFilterChange('municipality', newValue)}
                loading={municipalitiesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Изберете община..."
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            </Grid>

            {/* Населено място */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                options={towns}
                value={localFilters.town || null}
                onChange={(event, newValue) => handleLocalFilterChange('town', newValue)}
                loading={townsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Изберете населено място..."
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}>
            {hasActiveFilters && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Clear />}
                onClick={handleClearFilters}
                sx={{ 
                  minWidth: 120,
                  fontWeight: 600
                }}
              >
                Изчисти
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              startIcon={<Search />}
              onClick={handleApplyFilters}
              sx={{ 
                minWidth: 120,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6b3f8f 100%)',
                }
              }}
            >
              Търси
            </Button>
          </Box>

          <Box sx={{ 
            mt: 3, 
            pt: 2, 
            borderTop: '1px solid #e9ecef',
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Sort sx={{ color: 'text.secondary', fontSize: 20 }} />
              <TextField
                select
                label="По..."
                size="small"
                value={sort.sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="name">име</MenuItem>
                <MenuItem value="town">населено място</MenuItem>
                <MenuItem value="municipality">община</MenuItem>
                <MenuItem value="region">област</MenuItem>
              </TextField>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ fontSize: 20 }}>📊</Box>
                <TextField
                  select
                  label="Резултата на страница"
                  size="small"
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  sx={{ minWidth: 80 }}
                >
                  <MenuItem value={12}>12</MenuItem>
                  <MenuItem value={24}>24</MenuItem>
                  <MenuItem value={48}>48</MenuItem>
                  <MenuItem value={96}>96</MenuItem>
                </TextField>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ChitalishteFilters;